/**
 * 004 — Motor de plantilla compartido (anatomía idéntica de los 7 `.dc.html`).
 * Toma la config de un estilo (paleta/tipografía/ornamentos) y reproduce fielmente
 * la composición. Cada estilo tiene su archivo delgado que llama aquí con su config.
 */
import type { CSSProperties, ReactNode } from 'react';
import {
  CoverVeil, Photo, DateBlock, Countdown, GuestPass, Icon, Button, Itinerary,
  DressCode, NoteCard, GiftCard, GiftList, RsvpForm, Monogram, Filigree,
} from '@/invitation/ds';
import { deriveFields } from '@/invitation/payload/derived';
import { EVENTO_META, ROTULOS, NOTA_NINOS, NOTA_FOTOS, REGALO_TARJETAS, vestimenta as vestimentaCat } from '@/invitation/payload/system-catalog';
import {
  resolveHistoriaTexto, resolveHistoriaFrase, resolveRegalosMensaje, resolveEventoNota,
  resolveMomento, resolveVestimentaCopy,
} from '@/invitation/payload/resolve';
import type { TemplateProps } from './types';
import { orderedCouple } from '@/invitation/render/coupleOrder';
import { buildCalendarUrlFromPayload } from '@/invitation/render/calendarUrl';
import RsvpCerrado from '@/invitation/render/RsvpCerrado';
import type { EstiloConfig } from './configs';

const SEC_PAD = 'clamp(56px,6.4vw,102px) clamp(24px,6vw,96px)';

export function BodaTemplate({ cfg, payload, open = true, onEnter, onRsvp, rsvpSubmitted, rsvpClosed, coordinador, album }: TemplateProps & { cfg: EstiloConfig }) {
  const { c, f, fil, themeClass, headingKind } = cfg;
  const disp = headingKind === 'display';
  const p = payload;
  const i = p.interruptores;
  const d = deriveFields(p.boda.fecha, p.novia.nombreCorto, p.novio.nombreCorto);
  const galeria = p.fotos?.galeria ?? [];
  const sede = p.fotos?.sedes ?? {};
  const portadaImg = p.fotos?.portada || galeria[0];
  const parejaImg = p.fotos?.pareja || galeria[1] || galeria[0];
  const RATIOS = ['2 / 3', '3 / 2', '1 / 1', '4 / 5', '3 / 2', '2 / 3', '16 / 10', '5 / 4'];
  const openMap = (url?: string) => url && window.open(url, '_blank');

  const Amp = ({ light }: { light?: boolean }) => <span style={{ fontSize: '.62em', color: light ? c.accentLight : c.accent, verticalAlign: '.12em' }}>&amp;</span>;
  const { primero: n1, segundo: n2 } = orderedCouple(p);
  const calendarUrl = buildCalendarUrlFromPayload(p);
  const Names = ({ light }: { light?: boolean }) => (<>{n1.nombreCorto} <Amp light={light} /> {n2.nombreCorto}</>);

  function Diamond({ color = c.accentSoft, max = 200 }: { color?: string; max?: number }) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, width: '100%', maxWidth: max, margin: '0 auto' }}>
        <span style={{ flex: 1, height: 1, background: `linear-gradient(90deg,transparent,${color})` }} />
        <span style={{ width: 5, height: 5, background: color, transform: 'rotate(45deg)', flex: 'none' }} />
        <span style={{ flex: 1, height: 1, background: `linear-gradient(90deg,${color},transparent)` }} />
      </div>
    );
  }
  function Script({ children, cursiveSize = 'clamp(34px,4vw,54px)', color = c.accent }: { children: ReactNode; cursiveSize?: string; color?: string }) {
    if (disp) return <p style={{ margin: 0, fontFamily: f.script, textTransform: 'uppercase', letterSpacing: '.16em', fontSize: 'clamp(19px,2.3vw,30px)', lineHeight: 1.3, color }}>{children}</p>;
    return <p style={{ margin: 0, fontFamily: f.script, fontSize: cursiveSize, lineHeight: 1.28, padding: '0 .1em .12em', color }}>{children}</p>;
  }
  const wrap = (extra?: CSSProperties): CSSProperties => ({ position: 'relative', maxWidth: 1080, margin: '0 auto', display: 'grid', justifyItems: 'center', gap: 'clamp(20px,2.4vw,32px)', textAlign: 'center', ...extra });
  const Fil = ({ name, style }: { name?: string; style: CSSProperties }) => (name ? <div style={{ position: 'absolute', color: c.accent, pointerEvents: 'none', ...style }}><Filigree name={name} width={style.width as number ?? 360} /></div> : null);

  return (
    <div className={themeClass} style={{ background: c.bg, color: c.ink, fontFamily: f.body, fontWeight: 300, overflowX: 'hidden' }}>
      <style>{`
        .igt-split{grid-template-columns:1fr}.igt-w3{grid-template-columns:1fr}.igt-masonry{columns:1}.igt-fam{grid-template-columns:1fr}
        @media(min-width:560px){.igt-masonry{columns:2}.igt-fam{grid-template-columns:repeat(2,1fr)}}
        @media(min-width:900px){.igt-split{grid-template-columns:1.15fr .85fr}.igt-w3{grid-template-columns:1fr auto 1fr}.igt-masonry{columns:3}}
      `}</style>

      <CoverVeil names={<Names />} eyebrow={ROTULOS.sobreEyebrow} date={d.fechaLarga} image={portadaImg} cta={ROTULOS.sobreCta} onEnter={onEnter} open={open} />

      {/* Portada */}
      <section style={{ position: 'relative', minHeight: '92svh', display: 'grid', alignItems: 'end', justifyItems: 'center', background: c.dark, overflow: 'hidden', padding: 'clamp(64px,9vw,120px) clamp(24px,6vw,96px) clamp(88px,10vw,140px)' }}>
        {portadaImg && <div style={{ position: 'absolute', inset: 0 }}><Photo fill src={portadaImg} alt="" /></div>}
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(78% 58% at 50% 30%,rgba(0,0,0,.1),rgba(0,0,0,.55) 100%)' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg,rgba(0,0,0,.4) 0%,transparent 26%,rgba(0,0,0,.3) 52%,rgba(0,0,0,.85) 100%)' }} />
        <div style={{ position: 'relative', textAlign: 'center', display: 'grid', gap: 'clamp(18px,2.2vw,30px)', justifyItems: 'center', maxWidth: 1040 }}>
          <p style={{ margin: 0, fontFamily: f.serif, fontStyle: 'italic', fontSize: 'clamp(17px,1.5vw,22px)', color: c.onDark, opacity: 0.75 }}>{ROTULOS.portadaFrase}</p>
          {/* h2: el h1 de la página es el del CoverVeil (mismos nombres). Evita el
              duplicado de <h1>. Estilos inline → sin cambio visual. */}
          {disp
            ? <h2 style={{ margin: 0, fontFamily: f.script, textTransform: 'uppercase', letterSpacing: '.08em', fontWeight: 500, fontSize: 'clamp(34px,8vw,112px)', lineHeight: 1.05, color: c.onDark }}><Names light /></h2>
            : <h2 style={{ margin: 0, fontFamily: f.script, fontWeight: 400, fontSize: 'clamp(48px,12.5vw,196px)', lineHeight: .94, color: c.onDark, textShadow: '0 22px 60px rgba(0,0,0,.35)' }}><Names light /></h2>}
          <Diamond color="rgba(255,255,255,.5)" max={300} />
          {d.valida && <p style={{ margin: 0, fontFamily: f.caps, fontSize: 'clamp(10px,.92vw,13px)', letterSpacing: '.38em', textTransform: 'uppercase', color: c.onDark, opacity: 0.75 }}>{d.fechaNumerica}</p>}
        </div>
      </section>

      {/* Historia */}
      <section style={{ position: 'relative', padding: SEC_PAD, overflow: 'hidden' }}>
        <Fil name={fil.historia} style={{ top: '6%', left: -70, opacity: .07, width: 420 }} />
        {fil.historia && <div style={{ position: 'absolute', bottom: '8%', right: -70, color: c.accent, opacity: .07, pointerEvents: 'none' }}><Filigree name={fil.historia} width={420} flipX /></div>}
        <div style={wrap({ maxWidth: 740 })}>
          <Script>{ROTULOS.historia}</Script>
          <Diamond />
          <p style={{ margin: 0, fontFamily: f.serif, fontStyle: 'italic', fontWeight: 300, fontSize: 'clamp(20px,2vw,29px)', lineHeight: 1.78, color: c.sec }}>{resolveHistoriaTexto(p.historia?.texto)}</p>
        </div>
      </section>

      {/* Retratos */}
      {i.retratos === 'pareja' && parejaImg && (
        <section style={{ padding: '0 clamp(24px,6vw,110px) clamp(56px,6.4vw,102px)' }}>
          <div className="igt-split" style={{ maxWidth: 1180, margin: '0 auto', display: 'grid', gap: 'clamp(26px,3vw,48px)', alignItems: 'center' }}>
            <div style={{ width: '100%', maxWidth: 340, margin: '0 auto', boxShadow: '0 46px 70px -54px rgba(0,0,0,.55)' }}><Photo ratio="2 / 3" shape="arch" src={parejaImg} alt="" /></div>
            <div style={{ display: 'grid', gap: 'clamp(16px,1.8vw,26px)', justifyItems: 'start', textAlign: 'left' }}>
              <p style={{ margin: 0, fontFamily: f.serif, fontStyle: 'italic', fontSize: 'clamp(16px,1.4vw,20px)', color: c.muted }}>Los novios</p>
              <p style={{ margin: 0, fontFamily: f.script, fontSize: disp ? 'clamp(26px,3vw,42px)' : 'clamp(46px,5.4vw,82px)', textTransform: disp ? 'uppercase' : 'none', letterSpacing: disp ? '.06em' : undefined, lineHeight: 1.16, color: c.ink }}>
                {n1.nombreCompleto || n1.nombreCorto}<br /><Amp /> {n2.nombreCompleto || n2.nombreCorto}
              </p>
              <span style={{ width: 'min(220px,50%)', height: 1, background: `linear-gradient(90deg,${c.accentSoft},transparent)` }} />
              <p style={{ margin: 0, maxWidth: 400, fontFamily: f.serif, fontStyle: 'italic', fontSize: 'clamp(17px,1.5vw,22px)', lineHeight: 1.8, color: c.sec }}>{resolveHistoriaFrase(p.historia?.frase)}</p>
            </div>
          </div>
        </section>
      )}
      {i.retratos === 'individuales' && (p.fotos?.novia || p.fotos?.novio) && (
        <section style={{ padding: '0 clamp(24px,6vw,110px) clamp(56px,6.4vw,102px)' }}>
          <div className="igt-w3" style={{ maxWidth: 1180, margin: '0 auto', display: 'grid', gap: 'clamp(24px,2.8vw,42px)', alignItems: 'center' }}>
            <figure style={{ margin: 0, display: 'grid', gap: 22, justifyItems: 'center' }}>
              {p.fotos?.novia && <div style={{ width: '100%', maxWidth: 250, boxShadow: '0 40px 62px -50px rgba(0,0,0,.5)' }}><Photo ratio="2 / 3" shape="arch" src={p.fotos.novia} alt="" /></div>}
              <figcaption style={{ textAlign: 'center', display: 'grid', gap: 6 }}>
                <span style={{ fontFamily: f.serif, fontStyle: 'italic', fontSize: 16, color: c.muted }}>La novia</span>
                <span style={{ fontFamily: f.script, fontSize: 'clamp(28px,3vw,44px)', color: c.ink, textTransform: disp ? 'uppercase' : 'none' }}>{p.novia.nombreCompleto || p.novia.nombreCorto}</span>
              </figcaption>
            </figure>
            <div style={{ display: 'grid', justifyItems: 'center', gap: 14 }}>
              <span style={{ width: 1, height: 'clamp(0px,5vw,74px)', background: `linear-gradient(180deg,transparent,${c.accentSoft})` }} />
              <span style={{ fontFamily: f.script, fontSize: 'clamp(40px,5vw,72px)', color: c.accent }}>&amp;</span>
              <span style={{ width: 1, height: 'clamp(0px,5vw,74px)', background: `linear-gradient(180deg,${c.accentSoft},transparent)` }} />
            </div>
            <figure style={{ margin: 0, display: 'grid', gap: 22, justifyItems: 'center' }}>
              {p.fotos?.novio && <div style={{ width: '100%', maxWidth: 250, boxShadow: '0 40px 62px -50px rgba(0,0,0,.5)' }}><Photo ratio="2 / 3" shape="arch" src={p.fotos.novio} alt="" /></div>}
              <figcaption style={{ textAlign: 'center', display: 'grid', gap: 6 }}>
                <span style={{ fontFamily: f.serif, fontStyle: 'italic', fontSize: 16, color: c.muted }}>El novio</span>
                <span style={{ fontFamily: f.script, fontSize: 'clamp(28px,3vw,44px)', color: c.ink, textTransform: disp ? 'uppercase' : 'none' }}>{p.novio.nombreCompleto || p.novio.nombreCorto}</span>
              </figcaption>
            </figure>
          </div>
        </section>
      )}

      {/* Reserva la fecha */}
      {d.valida && (
        <section style={{ position: 'relative', padding: '0 clamp(24px,6vw,110px) clamp(56px,6.4vw,102px)', overflow: 'hidden' }}>
          <Fil name={fil.fecha} style={{ top: '50%', left: '50%', transform: 'translate(-50%,-50%)', opacity: .05, width: 430 }} />
          <div style={wrap({ maxWidth: 820 })}>
            <Script>{ROTULOS.reservaFecha}</Script>
            <Diamond />
            <DateBlock weekday={d.diaSemana} day={d.dia} monthYear={`${d.mesNombre} ${d.anio}`} time={d.horaEnLetra} />
            {p.boda.fecha && <div style={{ width: '100%', maxWidth: 440 }}><Countdown target={p.boda.fecha} /></div>}
            {calendarUrl && <Button variant="outline" size="md" onClick={() => window.open(calendarUrl, '_blank')}>{ROTULOS.reservaCta}</Button>}
          </div>
        </section>
      )}

      {/* Pase */}
      {p.invitado && (
        <section style={{ padding: '0 clamp(24px,6vw,110px) clamp(56px,6.4vw,102px)' }}>
          <div style={{ maxWidth: 560, margin: '0 auto' }}><GuestPass guest={p.invitado.nombre} passes={p.invitado.pases} /></div>
        </section>
      )}

      {/* Con la bendición de */}
      {p.familia && p.familia.length > 0 && (
        <section style={{ position: 'relative', overflow: 'hidden', padding: '0 clamp(24px,6vw,110px) clamp(56px,6.4vw,102px)', textAlign: 'center' }}>
          <Fil name={fil.familia} style={{ top: '14%', right: -40, opacity: .06, width: 360 }} />
          <div style={wrap({ maxWidth: 1080 })}>
            <Script cursiveSize="clamp(32px,3.6vw,48px)">{ROTULOS.bendicion}</Script>
            <Diamond />
            <div className="igt-fam" style={{ width: '100%', display: 'grid', gap: 'clamp(20px,2.2vw,30px)', marginTop: 8, maxWidth: 720 }}>
              {p.familia.map((fm, idx) => (
                <div key={idx} style={{ display: 'grid', gap: 8 }}>
                  <span style={{ fontFamily: f.serif, fontStyle: 'italic', fontSize: 16, color: c.muted }}>{fm.rol}</span>
                  <span style={{ fontFamily: f.serif, fontSize: 'clamp(18px,1.6vw,22px)', lineHeight: 1.6, color: c.ink }}>{fm.nombre}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Eventos (oscuro) */}
      {p.eventos && p.eventos.length > 0 && (
        <section style={{ position: 'relative', padding: SEC_PAD, background: c.dark, color: c.onDark, overflow: 'hidden' }}>
          <div style={{ position: 'relative', maxWidth: 1180, margin: '0 auto', display: 'grid', justifyItems: 'center', gap: 'clamp(30px,3.2vw,46px)' }}>
            <div style={{ display: 'grid', justifyItems: 'center', gap: 18, textAlign: 'center', width: '100%' }}>
              <Script cursiveSize="clamp(36px,4.2vw,58px)" color={c.accentLight}>{ROTULOS.eventos}</Script>
              <Diamond color={c.accentLight} />
            </div>
            <div style={{ width: '100%', display: 'grid', gap: 'clamp(26px,3vw,48px)', alignItems: 'start', gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))' }}>
              {p.eventos.map((e, idx) => (
                <article key={idx} style={{ display: 'grid', gap: 22, justifyItems: 'center', textAlign: 'center' }}>
                  {i.fotosLugares && sede[e.tipo] && <div style={{ width: '100%', maxWidth: 270, margin: '0 auto', boxShadow: '0 40px 60px -50px rgba(0,0,0,.85)' }}><Photo ratio="2 / 3" shape="arch" src={sede[e.tipo]} alt="" /></div>}
                  <Icon name={EVENTO_META[e.tipo].icon} size={30} stroke={c.accentLight} />
                  <h3 style={{ margin: 0, fontFamily: f.script, fontWeight: 400, fontSize: disp ? 'clamp(20px,2.2vw,28px)' : 'clamp(32px,3.4vw,46px)', textTransform: disp ? 'uppercase' : 'none', letterSpacing: disp ? '.12em' : undefined, lineHeight: 1.28, color: c.onDark }}>{EVENTO_META[e.tipo].titulo}</h3>
                  {e.hora && <p style={{ margin: 0, fontFamily: f.serif, fontStyle: 'italic', fontSize: 'clamp(17px,1.5vw,22px)', color: c.accentLight }}>{e.hora}</p>}
                  {e.sede && <p style={{ margin: 0, fontFamily: f.serif, fontSize: 'clamp(18px,1.6vw,23px)', lineHeight: 1.5 }}>{e.sede}</p>}
                  <p style={{ margin: 0, fontSize: 14, lineHeight: 1.9, opacity: .62, maxWidth: 290 }}>{e.direccion}{e.direccion && <br />}<em style={{ fontFamily: f.serif, fontSize: 16 }}>{resolveEventoNota(e.tipo, e.nota)}</em></p>
                  {e.mapaUrl && <Button variant="outline" size="sm" tone="onDark" onClick={() => openMap(e.mapaUrl)}>{ROTULOS.eventoAccion}</Button>}
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Itinerario */}
      {p.itinerario && p.itinerario.length > 0 && (
        <section style={{ overflow: 'hidden', padding: SEC_PAD }}>
          <div style={{ maxWidth: 1180, margin: '0 auto', display: 'grid', justifyItems: 'center', gap: 'clamp(26px,2.8vw,38px)' }}>
            <Script>{ROTULOS.itinerario}</Script>
            <Diamond />
            <div style={{ width: '100%' }}><Itinerary layout="horizontal" steps={p.itinerario.map((it) => { const m = resolveMomento(it.momento, it.momentoLibre); return { icon: m.icon, time: it.hora, label: m.nombre, detail: it.detalle }; })} /></div>
          </div>
        </section>
      )}

      {/* Vestimenta */}
      {p.vestimenta?.codigo && (
        <section style={{ position: 'relative', padding: '0 clamp(24px,6vw,110px) clamp(56px,6.4vw,102px)', overflow: 'hidden' }}>
          {i.fotoVestimenta && p.fotos?.vestimenta ? (
            <div style={{ maxWidth: 1040, margin: '0 auto', display: 'grid', gap: 'clamp(26px,3vw,48px)', alignItems: 'center', gridTemplateColumns: '1fr 1fr' }} className="igt-fam">
              <div style={{ width: '100%', maxWidth: 300, margin: '0 auto', boxShadow: '0 46px 70px -54px rgba(0,0,0,.5)' }}><Photo ratio="2 / 3" shape="arch" src={p.fotos.vestimenta} alt="" /></div>
              <div style={{ display: 'grid', justifyItems: 'center', gap: 26 }}>
                <Script cursiveSize="clamp(32px,3.6vw,48px)">{ROTULOS.vestimenta}</Script>
                <Diamond />
                <DressCode code={vestimentaCat(p.vestimenta.codigo)?.nombre ?? p.vestimenta.codigo} note={resolveVestimentaCopy(p.vestimenta.codigo)} palette={i.coloresSugeridos ? (p.vestimenta.paleta ?? []) : []} avoid={i.coloresReservados ? (p.vestimenta.reservados ?? []) : []} />
              </div>
            </div>
          ) : (
            <div style={{ maxWidth: 560, margin: '0 auto', display: 'grid', justifyItems: 'center', gap: 26 }}>
              <Script cursiveSize="clamp(32px,3.6vw,48px)">{ROTULOS.vestimenta}</Script>
              <DressCode code={vestimentaCat(p.vestimenta.codigo)?.nombre ?? p.vestimenta.codigo} note={resolveVestimentaCopy(p.vestimenta.codigo)} palette={i.coloresSugeridos ? (p.vestimenta.paleta ?? []) : []} avoid={i.coloresReservados ? (p.vestimenta.reservados ?? []) : []} />
            </div>
          )}
        </section>
      )}

      {/* Galería */}
      {i.galeria && galeria.length > 0 && (
        <section style={{ padding: '0 0 clamp(56px,6.4vw,102px)' }}>
          <div style={{ display: 'grid', justifyItems: 'center', gap: 10, textAlign: 'center', padding: '0 clamp(24px,6vw,110px) clamp(24px,2.6vw,36px)' }}>
            <Script>{ROTULOS.galeria}</Script>
            <Diamond />
          </div>
          <div className="igt-masonry" style={{ maxWidth: 1120, margin: '0 auto', padding: '0 clamp(12px,2.4vw,42px)', columnGap: 10 }}>
            {galeria.map((src, idx) => <div key={idx} style={{ breakInside: 'avoid', marginBottom: 10 }}><Photo ratio={RATIOS[idx % RATIOS.length]} src={src} alt="" /></div>)}
          </div>
        </section>
      )}

      {/* Notas */}
      <section style={{ position: 'relative', overflow: 'hidden', padding: '0 clamp(24px,6vw,110px) clamp(56px,6.4vw,102px)' }}>
        <Fil name={fil.notas} style={{ top: '44%', left: '50%', transform: 'translate(-50%,-50%)', opacity: .05, width: 300 }} />
        <div style={{ maxWidth: 1000, margin: '0 auto', display: 'grid', gap: 'clamp(24px,2.8vw,42px)', gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))' }}>
          <NoteCard icon="baby" title={i.ninosPermitidos ? 'Los niños son bienvenidos' : 'Sin niños'} body={i.ninosPermitidos ? NOTA_NINOS.permitidos : NOTA_NINOS.soloAdultos} framed={false} />
          {i.fotosInvitados && <NoteCard icon="camera" title="Comparte tus fotos" body={NOTA_FOTOS} hashtag={p.album?.hashtag} framed={false} />}
        </div>
        {i.fotosInvitados && album}
      </section>

      {/* Regalos */}
      {i.regalos && (
        <section style={{ position: 'relative', padding: 'clamp(52px,5.8vw,90px) clamp(24px,6vw,110px)', background: c.regalosBg }}>
          <div style={{ maxWidth: 1000, margin: '0 auto', display: 'grid', justifyItems: 'center', gap: 'clamp(22px,2.4vw,34px)', textAlign: 'center' }}>
            <Script cursiveSize="clamp(32px,3.8vw,50px)">{ROTULOS.regalos}</Script>
            <Diamond />
            <p style={{ margin: 0, maxWidth: 540, fontFamily: f.serif, fontStyle: 'italic', fontSize: 'clamp(18px,1.7vw,25px)', lineHeight: 1.75, color: c.sec }}>{resolveRegalosMensaje(p.regalos?.mensaje)}</p>
            <div style={{ width: '100%', display: 'grid', gap: 'clamp(20px,2.4vw,32px)', alignItems: 'stretch', marginTop: 10, gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))' }}>
              {i.mesaRegalos && p.regalos?.tiendaUrl && <GiftCard icon="store" title={REGALO_TARJETAS.mesaRegalos} description="Hemos preparado una lista en tienda." href={p.regalos.tiendaUrl} actionLabel="Abrir mesa" />}
              {i.transferencia && <GiftCard icon="credit-card" title={REGALO_TARJETAS.transferencia} description={[p.regalos?.banco, p.regalos?.titular].filter(Boolean).join(' · ')} detail={p.regalos?.cuenta} detailLabel="Número de cuenta" actionLabel="Copiar número" />}
              {i.transferenciaQR && p.regalos?.qrUrl && <GiftCard icon="credit-card" title={REGALO_TARJETAS.transferenciaQR} description="Desde la app de tu banco." qr={p.regalos.qrUrl} />}
              {i.efectivoSobre && <GiftCard icon="mail" title={REGALO_TARJETAS.efectivoSobre} description="Si prefieres efectivo, habrá un buzón a la entrada de la recepción." />}
            </div>
            {i.listaRegalos && p.regalos?.sugerencias && p.regalos.sugerencias.length > 0 && (
              <div style={{ width: '100%', maxWidth: 620, marginTop: 'clamp(18px,2vw,30px)', display: 'grid', gap: 'clamp(18px,2vw,26px)', justifyItems: 'center' }}>
                <p style={{ margin: 0, fontFamily: f.serif, fontStyle: 'italic', fontSize: 'clamp(17px,1.5vw,21px)', color: c.muted }}>Algunas ideas, por si te sirven</p>
                <GiftList items={p.regalos.sugerencias.map((s) => ({ name: s.nombre, price: s.precio, description: s.descripcion }))} currency={p.regalos.moneda ?? 'Bs'} />
              </div>
            )}
          </div>
        </section>
      )}

      {/* Confirmación */}
      {p.invitado && (
        <section style={{ position: 'relative', padding: SEC_PAD, overflow: 'hidden' }}>
          <Fil name={fil.confirmacion} style={{ top: '50%', left: '50%', transform: 'translate(-50%,-52%)', opacity: .05, width: 360 }} />
          <div style={{ position: 'relative', maxWidth: 520, margin: '0 auto' }}>
            <div style={{ textAlign: 'center', display: 'grid', justifyItems: 'center', gap: 16, marginBottom: 'clamp(34px,4vw,54px)' }}>
              <Script>{ROTULOS.confirmacion}</Script>
              <Diamond />
              {!rsvpClosed && p.rsvp?.fechaLimite && <p style={{ margin: 0, maxWidth: 400, fontFamily: f.serif, fontStyle: 'italic', fontSize: 'clamp(17px,1.5vw,22px)', lineHeight: 1.75, color: c.sec }}>Nos ayudaría muchísimo saberlo antes del {p.rsvp.fechaLimite}.</p>}
            </div>
            {rsvpClosed ? (
              <RsvpCerrado coordinadorUrl={coordinador?.url} coordinadorNombre={coordinador?.nombre} />
            ) : (
              <RsvpForm guest={p.invitado.nombre} maxPasses={p.invitado.pases} deadline={p.rsvp?.fechaLimite} submitted={rsvpSubmitted} onSubmit={onRsvp} />
            )}
          </div>
        </section>
      )}

      {/* Cierre */}
      <footer style={{ position: 'relative', padding: 'clamp(54px,6vw,94px) clamp(24px,6vw,110px)', textAlign: 'center', background: c.dark, color: c.onDark, overflow: 'hidden' }}>
        <div style={{ position: 'relative', display: 'grid', justifyItems: 'center', gap: 'clamp(24px,2.6vw,36px)' }}>
          <Monogram left={(n1.nombreCorto || '').charAt(0)} right={(n2.nombreCorto || '').charAt(0)} tone="onDark" />
          {disp
            ? <p style={{ margin: 0, fontFamily: f.script, textTransform: 'uppercase', letterSpacing: '.08em', fontSize: 'clamp(30px,5vw,60px)', color: c.onDark }}><Names light /></p>
            : <p style={{ margin: 0, fontFamily: f.script, fontSize: 'clamp(48px,5.6vw,84px)', lineHeight: 1.28, padding: '0 .1em .12em', color: c.onDark }}><Names light /></p>}
          <Diamond color={c.accentLight} max={220} />
          {d.valida && <p style={{ margin: 0, fontFamily: f.serif, fontStyle: 'italic', fontSize: 'clamp(17px,1.5vw,22px)', color: c.onDark, opacity: .6 }}>Gracias por acompañarnos · {d.fechaLarga}</p>}
        </div>
      </footer>
    </div>
  );
}
